import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import { FaChevronLeft, FaChevronRight, FaUsers } from 'react-icons/fa';
import { Event } from '../../../../types/event';
import { formatShiftCommunitySummary } from './scheduleCommunityUtils';

const DAY_KEYS = [
  'general.daysOfWeek.monday',
  'general.daysOfWeek.tuesday',
  'general.daysOfWeek.wednesday',
  'general.daysOfWeek.thursday',
  'general.daysOfWeek.friday',
  'general.daysOfWeek.saturday',
  'general.daysOfWeek.sunday',
] as const;

function formatShiftTime(date: Date, language: string): string {
  return new Intl.DateTimeFormat(language, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

function formatWeekRange(start: dayjs.Dayjs): string {
  const end = start.add(6, 'day');
  return `${start.format('D MMM')} - ${end.format('D MMM YYYY')}`;
}

type ScheduleWeekCalendarProps = {
  shifts: Event[];
  selectedWeekStart: dayjs.Dayjs;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  language: string;
  t: (key: string, options?: Record<string, unknown>) => string;
  onShiftClick: (shift: Event) => void;
};

type ShiftAssignee = {
  id: number;
  firstName: string;
  lastName: string;
  picture?: string;
};

function getAssigneeInitials(assignee: ShiftAssignee): string {
  const first = assignee.firstName?.charAt(0) ?? '';
  const last = assignee.lastName?.charAt(0) ?? '';
  return `${first}${last}`.toUpperCase() || '?';
}

function getShiftAssignees(shift: Event): ShiftAssignee[] {
  const participants: ShiftAssignee[] = shift.participants.map((participant) => ({
    id: participant.id,
    firstName: participant.firstName,
    lastName: participant.lastName,
    picture: participant.picture,
  }));

  const externalInvites: ShiftAssignee[] = shift.externalInvites.map((invite) => ({
    id: invite.externalInviteId,
    firstName: invite.firstName,
    lastName: invite.lastName,
    picture: invite.picture,
  }));

  return [...participants, ...externalInvites];
}

export default function ScheduleWeekCalendar({
  shifts,
  selectedWeekStart,
  onPrevWeek,
  onNextWeek,
  language,
  t,
  onShiftClick,
}: ScheduleWeekCalendarProps): JSX.Element {
  const today = dayjs().startOf('day');
  const weekLabel = t('page.event.schedules.weekOf', {
    date: formatWeekRange(selectedWeekStart),
  });
  const daysOfWeek = DAY_KEYS.map((dayKey) => t(dayKey));

  const shiftsByDay = useMemo(() => {
    const map = new Map<string, Event[]>();

    for (let i = 0; i < 7; i += 1) {
      map.set(selectedWeekStart.add(i, 'day').format('YYYY-MM-DD'), []);
    }

    shifts.forEach((shift) => {
      const key = dayjs(new Date(shift.startTime)).format('YYYY-MM-DD');
      if (map.has(key)) {
        map.get(key)?.push(shift);
      }
    });

    map.forEach((dayShifts) => {
      dayShifts.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
    });

    return map;
  }, [selectedWeekStart, shifts]);

  return (
    <div className="flex flex-1 flex-col gap-5 pb-4">
      <div className="flex items-center justify-center">
        <div className="flex items-center gap-4">
          <div className="flex items-center w-full gap-2">
            <button
              type="button"
              onClick={onPrevWeek}
              className="flex items-center justify-center rounded-[8px] bg-primary-900 p-2 text-secondary-200 transition-colors hover:bg-opacity-75">
              <FaChevronLeft className="w-4 h-4" />
            </button>
            <h2 className="text-2xl font-semibold text-primary-900">{weekLabel}</h2>
            <button
              type="button"
              onClick={onNextWeek}
              className="flex items-center justify-center rounded-[8px] bg-primary-900 p-2 text-secondary-200 transition-colors hover:bg-opacity-75">
              <FaChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden rounded-[12px] border border-secondary-200 border-opacity-50 bg-secondary-50 bg-opacity-5">
        <div className="grid grid-cols-7 border-b border-secondary-200">
          {daysOfWeek.map((day) => (
            <div
              key={day}
              className="border-l border-secondary-200 bg-secondary-200 bg-opacity-50 p-4 text-center first:border-l-0">
              <div className="text-sm font-semibold text-primary-900">{day}</div>
            </div>
          ))}
        </div>

        <div className="overflow-auto">
          <div className="grid min-h-[140px] grid-cols-7 border-b border-secondary-200 border-opacity-30">
            {Array.from({ length: 7 }, (_p, index) => {
              const day = selectedWeekStart.add(index, 'day');
              const dayKey = day.format('YYYY-MM-DD');
              const dayShifts = shiftsByDay.get(dayKey) ?? [];
              const isToday = day.isSame(today, 'day');

              return (
                <div
                  key={dayKey}
                  className={`relative flex min-h-[180px] flex-col border-l border-secondary-200 border-opacity-30 p-3 transition-all duration-500 first:border-l-0 ${isToday ? 'bg-primary bg-opacity-5' : ''}`}>
                  <div
                    className={`relative z-10 mb-3 text-right text-sm ${
                      isToday
                        ? 'ml-auto flex h-6 w-6 items-center justify-center rounded-full bg-primary font-semibold text-secondary-50'
                        : 'text-primary-900'
                    }`}>
                    {day.format('D')}
                  </div>

                  <div className="space-y-1">
                    {dayShifts.length === 0 ? (
                      <div className="rounded-[6px] bg-secondary-200 bg-opacity-50 py-1 text-center text-xs">
                        {t('page.event.schedules.noShiftsThisDay')}
                      </div>
                    ) : (
                      dayShifts.map((shift) => {
                        const assignees = getShiftAssignees(shift);
                        const visibleAssignees = assignees.slice(0, 3);
                        const remainingAssigneesCount = assignees.length - visibleAssignees.length;
                        const communitySummary = formatShiftCommunitySummary(shift, t);

                        return (
                          <button
                            key={shift.id}
                            type="button"
                            onClick={() => onShiftClick(shift)}
                            className="w-full rounded-[6px] p-1 text-left text-primary-900 text-xs cursor-pointer bg-secondary-200 bg-opacity-50">
                            <div className="truncate font-medium">{shift.title}</div>
                            <div className="truncate text-[11px] opacity-90">
                              {formatShiftTime(new Date(shift.startTime), language)}
                              {shift.endTime
                                ? ` - ${formatShiftTime(new Date(shift.endTime), language)}`
                                : ''}
                            </div>
                            {communitySummary && (
                              <div className="mt-1 truncate text-[10px] text-primary-600">
                                {communitySummary}
                              </div>
                            )}
                            {assignees.length > 0 && (
                              <div className="mt-1 flex items-center">
                                <div className="flex -space-x-1.5">
                                  {visibleAssignees.map((assignee) => (
                                    <div
                                      key={`assignee-${assignee.id}-${assignee.firstName}-${assignee.lastName}`}
                                      className="flex h-5 w-5 items-center justify-center overflow-hidden rounded-full border border-secondary-50 bg-primary-100 text-[9px] font-semibold text-primary-900"
                                      title={`${assignee.firstName} ${assignee.lastName}`}>
                                      {assignee.picture ? (
                                        <img
                                          src={assignee.picture}
                                          alt={`${assignee.firstName} ${assignee.lastName}`}
                                          className="h-full w-full object-cover"
                                        />
                                      ) : (
                                        getAssigneeInitials(assignee)
                                      )}
                                    </div>
                                  ))}
                                  {remainingAssigneesCount > 0 && (
                                    <div className="flex h-5 w-5 items-center justify-center rounded-full border border-secondary-50 bg-primary-200 text-[9px] font-semibold text-primary-900">
                                      +{remainingAssigneesCount}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] opacity-90">
                              <span className="flex items-center gap-1">
                                <FaUsers className="h-3 w-3" />
                                <span>
                                  {assignees.length}
                                  {shift.maximumAttendees ? ` / ${shift.maximumAttendees}` : ''}
                                </span>
                              </span>
                              {shift.isRecurring && (
                                <span className="rounded-[6px] bg-primary-300 px-1.5 py-0.5">
                                  {t(`component.eventDatesForm.frequency.${shift.frequency.toLowerCase()}`)}
                                </span>
                              )}
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
